import {
  ArrowsExpandIcon,
  CheckIcon,
  LightningBoltIcon,
  RefreshIcon,
  SparklesIcon,
  XIcon,
} from '@heroicons/react/outline';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React, { FC, useCallback, useState } from 'react';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';

import Button from '@/components/buttons/Button';
import AspectRatioSelector from '@/components/images/AspectRatioSelector';

import axiosInstance from '@/utils/httpClient';

// Transformation style presets with carefully crafted prompts
const TRANSFORMATION_PRESETS = [
  {
    id: 'professional',
    name: 'Professional',
    icon: '👔',
    description: 'Corporate headshot',
    prompt:
      'Transform this person into a professional corporate headshot. They should be wearing a tailored business suit or blazer. Clean, polished appearance with neutral studio background. Professional lighting. Keep their face and identity exactly the same.',
  },
  {
    id: 'news-anchor',
    name: 'News Anchor',
    icon: '📺',
    description: 'Broadcast ready',
    prompt:
      'Transform this person into a professional news anchor or TV presenter. They should be in a broadcast studio setting with professional attire. Confident, authoritative look with professional TV lighting. Keep their face and identity exactly the same.',
  },
  {
    id: 'creator',
    name: 'Content Creator',
    icon: '🎬',
    description: 'Modern influencer',
    prompt:
      'Transform this person into a content creator photo looking into camera, shoulder length framing, holding a small lavalier microphone near their mouth while speaking. Background: a cozy living room wall with a small gallery of framed photos. Soft daylight, neutral tones, chest-up framing, realistic texture. No phone visible. Keep their face and identity exactly the same.',
  },
  {
    id: 'fitness',
    name: 'Fitness Coach',
    icon: '💪',
    description: 'Athletic & healthy',
    prompt:
      'Transform this person into a fitness coach or wellness influencer. They should be in athletic wear in a gym or outdoor fitness setting. Energetic, healthy appearance with good lighting. Keep their face and identity exactly the same.',
  },
  {
    id: 'executive',
    name: 'Executive',
    icon: '🏢',
    description: 'C-suite leader',
    prompt:
      'Transform this person into a C-suite executive or business leader. Premium tailored suit, high-end office or executive setting. Commanding presence with sophisticated lighting. Keep their face and identity exactly the same.',
  },
  {
    id: 'custom',
    name: 'Custom',
    icon: '✨',
    description: 'Your vision',
    prompt: '',
  },
];

// Aspect ratio options optimized for video platforms
const ASPECT_RATIO_OPTIONS = [
  '9:16', // TikTok, Instagram Reels, YouTube Shorts (vertical)
  '1:1', // Instagram Feed, Facebook
  '4:5', // Instagram Portrait
  '16:9', // YouTube, LinkedIn
  '4:3', // Traditional video
  '3:4', // Portrait
];

// Platform hints for aspect ratios
const ASPECT_RATIO_HINTS: Record<string, string> = {
  '9:16': 'TikTok, Reels, Shorts',
  '1:1': 'Instagram Feed',
  '4:5': 'Instagram Portrait',
  '16:9': 'YouTube, LinkedIn',
  '4:3': 'Traditional',
  '3:4': 'Portrait',
};

interface ActorPhotoTransformProps {
  originalPhoto: string;
  onTransformComplete: (newPhotoUrl: string, aspectRatio: string) => void;
  onCancel: () => void;
  onUseOriginal: () => void;
}

export const ActorPhotoTransform: FC<ActorPhotoTransformProps> = ({
  originalPhoto,
  onTransformComplete,
  onCancel,
  onUseOriginal,
}) => {
  const { t } = useLocale('ai-talking-videos');

  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16'); // Default to vertical for videos
  const [isTransforming, setIsTransforming] = useState(false);
  const [transformedPhoto, setTransformedPhoto] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonPosition, setComparisonPosition] = useState(50);
  const [error, setError] = useState<string | null>(null);

  const selectedPresetData = TRANSFORMATION_PRESETS.find(
    (p) => p.id === selectedPreset,
  );

  const getTransformPrompt = useCallback(() => {
    if (selectedPreset === 'custom') {
      return customPrompt.trim();
    }
    return selectedPresetData?.prompt || '';
  }, [selectedPreset, customPrompt, selectedPresetData]);

  const canTransform =
    selectedPreset &&
    (selectedPreset !== 'custom' || customPrompt.trim().length > 10);

  const handleTransform = async () => {
    const prompt = getTransformPrompt();
    if (!prompt) {
      toast.error(t('Please select a style or enter a custom prompt'));
      return;
    }

    setIsTransforming(true);
    setError(null);

    try {
      const response = await axiosInstance.post('/api/images', {
        action: 'UPDATE_KONTEXT',
        image: originalPhoto,
        images: JSON.stringify([originalPhoto]),
        prompt: prompt,
        aspectRatio: aspectRatio,
        editModel: 'Nano Banana Pro',
        n: 1,
        resolution: '2K',
      });

      if (response.data?.images && response.data.images.length > 0) {
        setTransformedPhoto(response.data.images[0].image_url);
        setShowComparison(true);
      } else {
        throw new Error('No image returned');
      }
    } catch (err: any) {
      console.error('Transform error:', err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        t('Failed to transform photo. Please try again.');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsTransforming(false);
    }
  };

  const handleRetry = () => {
    setTransformedPhoto(null);
    setShowComparison(false);
    setError(null);
  };

  const handleAccept = () => {
    if (transformedPhoto) {
      onTransformComplete(transformedPhoto, aspectRatio);
    }
  };

  // Comparison slider for before/after
  const handleComparisonDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setComparisonPosition(percentage);
  };

  // Get aspect ratio class for preview
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case '9:16':
        return 'aspect-[9/16]';
      case '16:9':
        return 'aspect-[16/9]';
      case '4:5':
        return 'aspect-[4/5]';
      case '4:3':
        return 'aspect-[4/3]';
      case '3:4':
        return 'aspect-[3/4]';
      default:
        return 'aspect-square';
    }
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='text-center'>
        <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-500/25'>
          <SparklesIcon className='h-6 w-6 text-white' />
        </div>
        <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
          {t('Transform Your Photo')}
        </h3>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
          {t('Choose a style or describe how you want your actor to look')}
        </p>
      </div>

      {/* Comparison View (shown after transformation) */}
      {showComparison && transformedPhoto ? (
        <div className='space-y-4'>
          {/* Before/After Slider */}
          <div
            className={clsxm(
              'relative mx-auto max-w-md cursor-col-resize overflow-hidden rounded-2xl shadow-2xl',
              getAspectRatioClass(),
            )}
            onMouseMove={(e) => e.buttons === 1 && handleComparisonDrag(e)}
            onMouseDown={handleComparisonDrag}
          >
            {/* After (transformed) - full width */}
            <div className='absolute inset-0'>
              <Image
                src={transformedPhoto}
                alt='Transformed'
                fill
                className='object-cover'
              />
              <div className='absolute bottom-3 right-3 rounded-full bg-green-500/90 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm'>
                {t('After')}
              </div>
            </div>

            {/* Before (original) - clipped */}
            <div
              className='absolute inset-0 overflow-hidden'
              style={{ width: `${comparisonPosition}%` }}
            >
              <div
                className='relative h-full'
                style={{
                  width: `${comparisonPosition > 0 ? 100 / (comparisonPosition / 100) : 100}%`,
                }}
              >
                <Image
                  src={originalPhoto}
                  alt='Original'
                  fill
                  className='object-cover'
                />
              </div>
              <div className='absolute bottom-3 left-3 rounded-full bg-gray-800/90 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm'>
                {t('Before')}
              </div>
            </div>

            {/* Slider handle */}
            <div
              className='absolute top-0 bottom-0 z-10 w-1 cursor-col-resize bg-white shadow-lg'
              style={{
                left: `${comparisonPosition}%`,
                transform: 'translateX(-50%)',
              }}
            >
              <div className='absolute top-1/2 left-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-lg'>
                <ArrowsExpandIcon className='h-5 w-5 rotate-90 text-gray-600' />
              </div>
            </div>
          </div>

          {/* Transformation Info */}
          <div className='mx-auto max-w-md rounded-xl bg-gray-50 p-4 dark:bg-gray-800/50'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400'>
                <span className='text-lg'>{selectedPresetData?.icon}</span>
                <span className='font-medium'>{selectedPresetData?.name}</span>
                {selectedPreset !== 'custom' && (
                  <span className='text-gray-400'>•</span>
                )}
                <span className='text-gray-400'>
                  {selectedPreset === 'custom'
                    ? customPrompt.slice(0, 30) +
                      (customPrompt.length > 30 ? '...' : '')
                    : selectedPresetData?.description}
                </span>
              </div>
              <div className='flex items-center gap-1 rounded-lg bg-gray-200 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300'>
                {aspectRatio}
                <span className='text-gray-400'>•</span>
                <span className='text-gray-500'>
                  {ASPECT_RATIO_HINTS[aspectRatio]}
                </span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className='flex flex-col gap-3 sm:flex-row sm:justify-center'>
            <Button
              variant='outline'
              onClick={handleRetry}
              className='order-2 sm:order-1'
            >
              <RefreshIcon className='mr-2 h-4 w-4' />
              {t('Try Different Style')}
            </Button>
            <Button
              variant='dark'
              onClick={handleAccept}
              className='order-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 sm:order-2'
            >
              <CheckIcon className='mr-2 h-4 w-4' />
              {t('Use This Photo')}
            </Button>
          </div>

          <button
            onClick={onUseOriginal}
            className='mx-auto block text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          >
            {t('Use original photo instead')}
          </button>
        </div>
      ) : (
        <>
          {/* Original Photo Preview with Aspect Ratio */}
          <div className='flex flex-col items-center gap-4'>
            <div className='relative w-40'>
              <div
                className={clsxm(
                  'overflow-hidden rounded-2xl shadow-xl ring-4 ring-white dark:ring-gray-800',
                  getAspectRatioClass(),
                )}
              >
                <Image
                  src={originalPhoto}
                  alt='Original'
                  width={160}
                  height={284}
                  className='h-full w-full object-cover'
                />
              </div>
              {isTransforming && (
                <div className='absolute inset-0 flex items-center justify-center rounded-2xl bg-black/60 backdrop-blur-sm'>
                  <div className='text-center'>
                    <div className='relative mx-auto h-10 w-10'>
                      <div className='absolute inset-0 animate-ping rounded-full bg-purple-400 opacity-75' />
                      <div className='relative flex h-10 w-10 items-center justify-center rounded-full bg-purple-500'>
                        <SparklesIcon className='h-5 w-5 text-white' />
                      </div>
                    </div>
                    <p className='mt-2 text-xs font-medium text-white'>
                      {t('Transforming...')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Aspect Ratio Selector */}
            <div className='flex items-center gap-3'>
              <span className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                {t('Format')}:
              </span>
              <AspectRatioSelector
                value={aspectRatio}
                onChange={setAspectRatio}
                options={ASPECT_RATIO_OPTIONS}
                disabled={isTransforming}
              />
              {ASPECT_RATIO_HINTS[aspectRatio] && (
                <span className='text-xs text-gray-400 dark:text-gray-500'>
                  {ASPECT_RATIO_HINTS[aspectRatio]}
                </span>
              )}
            </div>
          </div>

          {/* Style Presets Grid */}
          <div className='grid grid-cols-2 gap-3 sm:grid-cols-3'>
            {TRANSFORMATION_PRESETS.map((preset) => {
              const isSelected = selectedPreset === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => setSelectedPreset(preset.id)}
                  disabled={isTransforming}
                  className={clsxm(
                    'group relative rounded-xl border-2 p-4 text-left transition-all duration-200',
                    isSelected
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-purple-600 dark:hover:bg-gray-800/50',
                    isTransforming && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <div className='flex items-start gap-3'>
                    <span className='text-2xl'>{preset.icon}</span>
                    <div className='flex-1 min-w-0'>
                      <p
                        className={clsxm(
                          'font-medium transition-colors',
                          isSelected
                            ? 'text-purple-700 dark:text-purple-300'
                            : 'text-gray-900 dark:text-white',
                        )}
                      >
                        {t(preset.name)}
                      </p>
                      <p className='mt-0.5 text-xs text-gray-500 dark:text-gray-400'>
                        {t(preset.description)}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className='absolute -right-1 -top-1'>
                      <div className='flex h-5 w-5 items-center justify-center rounded-full bg-purple-500'>
                        <CheckIcon className='h-3 w-3 text-white' />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Custom Prompt Input (shown when custom is selected) */}
          {selectedPreset === 'custom' && (
            <div className='animate-in fade-in slide-in-from-top-2 duration-200'>
              <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                {t('Describe the transformation')}
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder={t(
                  'e.g., "Make me look like a tech entrepreneur in a modern office with casual smart attire"',
                )}
                rows={3}
                maxLength={500}
                disabled={isTransforming}
                className='w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-purple-500 focus:ring-purple-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white'
              />
              <div className='mt-1 flex items-center justify-between text-xs text-gray-400'>
                <span>
                  {t('Tip: Be specific about clothing, setting, and style')}
                </span>
                <span>{customPrompt.length}/500</span>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className='rounded-xl bg-red-50 p-4 text-center text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400'>
              {error}
            </div>
          )}

          {/* Action buttons */}
          <div className='flex flex-col gap-3 sm:flex-row sm:justify-center'>
            <Button
              variant='outline'
              onClick={onCancel}
              disabled={isTransforming}
              className='order-2 sm:order-1'
            >
              <XIcon className='mr-2 h-4 w-4' />
              {t('Cancel')}
            </Button>
            <Button
              variant='dark'
              onClick={handleTransform}
              disabled={!canTransform || isTransforming}
              isLoading={isTransforming}
              className='order-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 sm:order-2'
            >
              {!isTransforming && (
                <LightningBoltIcon className='mr-2 h-4 w-4' />
              )}
              {isTransforming ? t('Transforming...') : t('Transform Photo')}
            </Button>
          </div>

          {/* Skip option */}
          <button
            onClick={onUseOriginal}
            disabled={isTransforming}
            className='mx-auto block text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-300'
          >
            {t('Skip and use original photo')}
          </button>

          {/* Credit info */}
          <p className='text-center text-xs text-gray-400 dark:text-gray-500'>
            {t('Uses 2 image credits per transformation')}
          </p>
        </>
      )}
    </div>
  );
};

export default ActorPhotoTransform;
